// Please do not convert to a functional component unless you know what you're doing.

import { useEffect, useState } from 'react';

import {
  Checkbox,
  FieldSet,
  FormField,
  FormHelp,
  Input,
  Label,
  LabeledControl,
  Legend,
  Text,
  Textarea,
} from '@zillow/constellation-10';

import type { Section } from '../../../models/index';
import { PAYMENT_METHOD } from '../../../constants/index';
import { parseElectronicPaymentOptions } from '../../../utils/stringParsing';
import { usePreSubmitCallback } from '../../../contexts/PreSubmitCallbackContext';

type Props = {
  section: Section;
  setSectionValues(keyValuePairs: object): void;
};

const { ZILLOW_PAYMENTS } = PAYMENT_METHOD.OTHER_CONSTANTS;
const { ELECTRONIC_SERVICE_TYPE, ELECTRONIC_IS_ENABLED, DIRECT_IS_ENABLED, CHECK_IS_ENABLED, CHECK_ADDRESS } =
  PAYMENT_METHOD.FIELD_NAME;

export function PaymentMethod({ section, setSectionValues }: Props) {
  const electronicPaymentSelected = section.getFieldValue(ELECTRONIC_IS_ENABLED);
  const electronicServiceTypeFieldValue = section.getFieldValue(ELECTRONIC_SERVICE_TYPE);
  const { hasZillowPayments, otherElectronicPaymentOptions } = parseElectronicPaymentOptions(
    electronicServiceTypeFieldValue,
  );

  const [zillowPaymentsSelected, setZillowPaymentsSelected] = useState(hasZillowPayments);
  const [electronicPaymentMaskSelected, setElectronicPaymentMaskSelected] = useState(
    electronicPaymentSelected && electronicServiceTypeFieldValue !== ZILLOW_PAYMENTS,
  );

  const { setPreSubmitCallback } = usePreSubmitCallback();

  function paymentMethodCallbackFunction() {
    const electronicServiceTypeFieldValue = parseElectronicPaymentOptions(
      section.getFieldValue(ELECTRONIC_SERVICE_TYPE),
    ).otherElectronicPaymentOptions;

    if (zillowPaymentsSelected) {
      if (electronicServiceTypeFieldValue !== '') {
        setSectionValues({
          [ELECTRONIC_SERVICE_TYPE]: `${ZILLOW_PAYMENTS}, ${electronicServiceTypeFieldValue}`,
        });
      } else {
        setSectionValues({
          [ELECTRONIC_SERVICE_TYPE]: `${ZILLOW_PAYMENTS}`,
          [ELECTRONIC_IS_ENABLED]: true,
        });
      }
    } else {
      if (electronicPaymentMaskSelected !== section.getFieldValue(ELECTRONIC_IS_ENABLED)) {
        setSectionValues({
          [ELECTRONIC_IS_ENABLED]: false,
        });
      }
    }
  }

  useEffect(() => {
    if (hasZillowPayments) {
      setSectionValues({
        [ELECTRONIC_SERVICE_TYPE]: otherElectronicPaymentOptions,
      });
    }

    setPreSubmitCallback(paymentMethodCallbackFunction);

    return () => {
      // Clean up callback on unmount
      setPreSubmitCallback(null);
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, []);

  function onSelectZillowPayment() {
    setZillowPaymentsSelected(!zillowPaymentsSelected);
  }

  function onSelectElectronic() {
    const electronicField = electronicPaymentMaskSelected;

    setElectronicPaymentMaskSelected(!electronicPaymentMaskSelected);

    setSectionValues({
      [ELECTRONIC_IS_ENABLED]: !electronicField,
      [ELECTRONIC_SERVICE_TYPE]: '',
    });
  }

  function onSelectDirect() {
    setSectionValues({
      [DIRECT_IS_ENABLED]: !section.getFieldValue(DIRECT_IS_ENABLED),
    });
  }

  function onSelectCheck() {
    const checkField = section.getFieldValue(CHECK_IS_ENABLED);

    setSectionValues({
      [CHECK_IS_ENABLED]: !checkField,
      [CHECK_ADDRESS]: '',
    });
  }

  function onAddressChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setSectionValues({
      [CHECK_ADDRESS]: event.currentTarget.value,
    });
  }

  function onElectronicServiceTypeChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setSectionValues({
      [ELECTRONIC_SERVICE_TYPE]: event.currentTarget.value,
    });
  }

  return (
    <>
      <FieldSet required legend={<Legend>Which forms of payment will you accept?</Legend>}>
        <LabeledControl
          size="lg"
          label={
            <Label>
              Zillow Payments
              <br />
              <Text css={{ color: 'text.subtle' }}>
                Tenant pays through our online service. Set up quickly using information from your lease.
              </Text>
            </Label>
          }
          control={<Checkbox checked={zillowPaymentsSelected} onChange={onSelectZillowPayment} />}
        />
        <LabeledControl
          size="lg"
          label={
            <Label>
              Other electronic payment
              <br />
              <Text css={{ color: 'text.subtle' }}>Tenant pays via third party online service of your choice.</Text>
            </Label>
          }
          control={<Checkbox checked={electronicPaymentMaskSelected} onChange={onSelectElectronic} />}
        />
        <LabeledControl
          size="lg"
          label={
            <Label>
              Direct deposit
              <br />
              <Text css={{ color: 'text.subtle' }}>
                Tenant transfers funds using bank info, including ACH, if you provide them.
              </Text>
            </Label>
          }
          control={
            <Checkbox
              checked={section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.DIRECT_IS_ENABLED)}
              onChange={onSelectDirect}
            />
          }
        />
        <LabeledControl
          size="lg"
          label={
            <Label>
              Check or money order
              <br />
              <Text css={{ color: 'text.subtle' }}>
                Tenant sends payment to either the landlord or the property manager.
              </Text>
            </Label>
          }
          control={
            <Checkbox
              checked={section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.CHECK_IS_ENABLED)}
              onChange={onSelectCheck}
            />
          }
        />
      </FieldSet>
      {electronicPaymentMaskSelected && (
        <FormField
          required
          css={{ marginTop: 'layout.tight' }}
          label={<Label id="payment-service">What electronic payment service would you like to use?</Label>}
          control={
            <Input
              onChange={onElectronicServiceTypeChange}
              value={section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.ELECTRONIC_SERVICE_TYPE)}
              aria-labelledby="payment-service"
              type="text"
            />
          }
        />
      )}
      {section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.CHECK_IS_ENABLED) && (
        <FormField
          required
          css={{ marginTop: 'layout.tight' }}
          label={
            <Label id="whom-send-check-or-money-order">To whom should the tenant send check or money order?</Label>
          }
          control={
            <Textarea
              error={section.hasFieldError(PAYMENT_METHOD.FIELD_NAME.CHECK_ADDRESS)}
              onChange={onAddressChange}
              value={section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.CHECK_ADDRESS)}
              rows={3}
              aria-labelledby="whom-send-check-or-money-order"
            />
          }
          description={<FormHelp>Recipient’s name, street, city, state, and ZIP code</FormHelp>}
        />
      )}
    </>
  );
}

export default PaymentMethod;
